import React from 'react';
import { ChartElement } from './AdvancedChart';
import './RightAdjustmentPanel.css';
import './ResponsiveStyles.css';

interface RightAdjustmentPanelProps {
  elements: ChartElement[];
  onAdjustElement: (elementId: string, adjustment: number) => void;
  isDarkMode: boolean;
  isActive: boolean;
  chartHeight?: number;
  uiScale?: number;
  isDateRangeLocked?: boolean;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const formatPercent = (ratio: number) => `${Math.round(ratio * 100)}%`;

const parsePercentToRatio = (text: string) => {
  const parsed = Number.parseFloat(text.replace('%', '').trim());
  if (!Number.isFinite(parsed)) return undefined;
  return parsed / 100;
};

const RightAdjustmentPanel: React.FC<RightAdjustmentPanelProps> = ({
  elements,
  onAdjustElement,
  isDarkMode,
  isActive,
  chartHeight = 50,
  uiScale,
  isDateRangeLocked = false,
}) => {
  const [exitAdjustments, setExitAdjustments] = React.useState<Record<string, number>>({});
  const [entryAdjustments, setEntryAdjustments] = React.useState<Record<string, number>>({});
  const [exitAdjustmentTexts, setExitAdjustmentTexts] = React.useState<Record<string, string>>({});
  const [entryAdjustmentTexts, setEntryAdjustmentTexts] = React.useState<Record<string, string>>({});
  const [exitSliderLength, setExitSliderLength] = React.useState<number | null>(null);
  const [entrySliderLength, setEntrySliderLength] = React.useState<number | null>(null);
  const exitSliderTrackRef = React.useRef<HTMLDivElement | null>(null);
  const entrySliderTrackRef = React.useRef<HTMLDivElement | null>(null);

  const isZoomed125 = window.devicePixelRatio >= 1.25;
  const scale = clamp(uiScale ?? chartHeight / 50, 0.5, 1.25);

  const rem = (value: number, min?: number, max?: number) => {
    const scaled = value * scale;
    if (min === undefined && max === undefined) return `${scaled}rem`;
    return `${clamp(scaled, min ?? -Infinity, max ?? Infinity)}rem`;
  };

  // Unscaled px helper for offsets that must remain effective during shrink.
  const pxRaw = (value: number, min?: number, max?: number) => {
    if (min === undefined && max === undefined) return `${value}px`;
    return `${clamp(value, min ?? -Infinity, max ?? Infinity)}px`;
  };

  const pxScaled = (value: number, min?: number, max?: number) => {
    const scaled = value * scale;
    if (min === undefined && max === undefined) return `${scaled}px`;
    return `${clamp(scaled, min ?? -Infinity, max ?? Infinity)}px`;
  };

  const backgroundColor = isDarkMode ? '#1f2937' : '#F6F6F6';
  const textColor = isDarkMode ? '#e5e7eb' : '#0D0D0D';
  const borderColor = isDarkMode ? '#374151' : '#B3B3B3';

  const exitPriceElement =
    elements.find((e) => e.id === 'entry-point-2' && e.visible) ??
    elements.find((e) => e.id === 'entry-point-2-ocf' && e.visible) ??
    elements.find((e) => e.id === 'entry-point-2') ??
    elements.find((e) => e.id === 'entry-point-2-ocf');
  const entryPriceElement =
    elements.find((e) => e.id === 'entry-point-1' && e.visible) ??
    elements.find((e) => e.id === 'entry-point-1-ocf' && e.visible) ??
    elements.find((e) => e.id === 'entry-point-1') ??
    elements.find((e) => e.id === 'entry-point-1-ocf');

  const exitMin = 0.05;
  const exitMax = 0.95;
  const entryMin = 0.05;
  const entryMax = 0.95;

  const isExitPriceVisible = elements.some((e) => (e.id === 'entry-point-2' || e.id === 'entry-point-2-ocf') && e.visible);
  const isExitDisabled = !isExitPriceVisible || !isActive || isDateRangeLocked;
  const exitDisabledOpacity = isExitDisabled ? 0.6 : 1;
  const exitDisabledPointerEvents = isExitDisabled ? 'none' : 'auto';
  const exitTextColor = isExitDisabled
    ? (isDarkMode ? '#9ca3af' : '#6b7280')
    : (isDarkMode ? '#F9FAFB' : textColor);

  const isEntryPriceVisible = elements.some((e) => (e.id === 'entry-point-1' || e.id === 'entry-point-1-ocf') && e.visible);
  const entryPriceColor = entryPriceElement?.color || '#10b981';
  const isEntryDisabled = !isEntryPriceVisible || !isActive || isDateRangeLocked;
  const entryTextColor = isEntryDisabled
    ? (isDarkMode ? '#9ca3af' : '#6b7280')
    : (isDarkMode ? '#F9FAFB' : textColor);

  React.useEffect(() => {
    const exitInitial: Record<string, number> = {};
    const entryInitial: Record<string, number> = {};
    const exitTextInitial: Record<string, string> = {};
    const entryTextInitial: Record<string, string> = {};
    for (const element of elements) {
      const raw = element.adjustment || 0;
      const exitValue = (element.id === 'entry-point-2' || element.id === 'entry-point-2-ocf')
        ? clamp(raw, exitMin, exitMax)
        : raw;
      const entryValue = (element.id === 'entry-point-1' || element.id === 'entry-point-1-ocf')
        ? clamp(raw, entryMin, entryMax)
        : raw;
      exitInitial[element.id] = exitValue;
      entryInitial[element.id] = entryValue;
      exitTextInitial[element.id] = formatPercent(exitValue);
      entryTextInitial[element.id] = formatPercent(entryValue);
    }
    setExitAdjustments(exitInitial);
    setEntryAdjustments(entryInitial);
    setExitAdjustmentTexts(exitTextInitial);
    setEntryAdjustmentTexts(entryTextInitial);
  }, [elements]);

  React.useLayoutEffect(() => {
    if (!exitSliderTrackRef.current) return;
    const target = exitSliderTrackRef.current;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setExitSliderLength(Math.max(0, entry.contentRect.height));
      }
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [scale]);

  React.useLayoutEffect(() => {
    if (!entrySliderTrackRef.current) return;
    const target = entrySliderTrackRef.current;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setEntrySliderLength(Math.max(0, entry.contentRect.height));
      }
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [scale]);

  const shrinkT = clamp((1 - scale) / 0.25, 0, 1);

  const sliderContainerPadding = `0rem 0rem`;
  const labelMarginTop = isZoomed125 ? rem(-0.45) : rem(1);
  const exitBottomLabelMarginTop = '0px';
  const entry95MarginTop = '0px';
  const sliderAreaGap = '0px';

  // Make the two sections exactly identical in height (1:1)
  const exitSectionFlexGrow = 1;
  const entrySectionFlexGrow = 1;

  const panelWidth = '3.9375rem';
  const sliderWidth = `${clamp(6.5 * scale ** 1.15, 2.6, 7.5)}rem`;

  // Thumb/track shrink more aggressively (prevents overlaps on small shrinks).
  const thumbPx = clamp(16 * scale ** 1.75, 5.5, 22);
  const trackPx = clamp(3 * scale ** 1.2, 1, 5);
  const controlHeightPx = clamp(30 * scale ** 1.1, 14, 40);
  const lineThicknessPx = clamp(2 * scale ** 1.1, 1, 4);
  const thumbBorderPx = clamp(2 * scale ** 1.05, 1, 3);

  const sliderCssVars = {
    ['--exit-slider-thumb-size' as any]: `${thumbPx}px`,
    ['--exit-slider-thumb-border' as any]: `${thumbBorderPx}px`,
    ['--exit-slider-track-height' as any]: `${trackPx}px`,
    ['--exit-slider-control-height' as any]: `${controlHeightPx}px`,
    ['--exit-slider-line-thickness' as any]: `${lineThicknessPx}px`,
    ['--exit-slider-width-125' as any]: sliderWidth,
  };

  // 0.00 box bottom offset inside its own slider area.
  // On slight shrink keep it low to avoid overlapping the bottom label.
  const valueBoxBottom = pxRaw(6 + 1.5 * shrinkT, 4, 10);
  const exitValueBoxBottom = `calc(${valueBoxBottom} + 6px)`;
  const entryValueBoxBottom = `calc(${valueBoxBottom} + 4px)`;

  // Keep the divider in-flow (no negative margins) so Exit/Entry stay exactly 1:1.
  const dividerMarginTop = '0px';

  const commonSectionStyle: React.CSSProperties = {
    width: panelWidth,
    backgroundColor,
    borderRadius: '0rem',
    border: `1px solid ${borderColor}`,
    textAlign: 'center',
    transition: 'opacity 0.3s ease',
    pointerEvents: isActive ? 'auto' : 'none',
  };

  const renderValueBox = (
    inputId: string,
    textValue: string,
    onChange: (nextText: string) => void,
    onBlur: () => void,
    disabled: boolean,
    disabledOpacity: number,
    bottomOverride?: string,
    useAbsolute: boolean = true,
    lockedDisabled: boolean = false
  ) => (
    <div
      style={{
        width: rem(2.4, 1.2, 3.1),
        position: useAbsolute ? 'absolute' : 'static',
        left: useAbsolute ? '50%' : undefined,
        transform: useAbsolute ? 'translateX(-50%)' : undefined,
        bottom: useAbsolute ? bottomOverride ?? valueBoxBottom : undefined,
        borderRadius: rem(0.25),
        border: `1px solid ${borderColor}`,
        backgroundColor: lockedDisabled
          ? (isDarkMode ? '#374151' : '#E5E7EB')
          : (isDarkMode ? (disabled ? '#334155' : '#111827') : '#ffffff'),
        opacity: disabledOpacity,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        boxSizing: 'border-box',
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <input
        id={inputId}
        type="text"
        inputMode="numeric"
        value={textValue}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        style={{
          width: '100%',
          padding: `${rem(0.12)} ${rem(0.25)}`,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: lockedDisabled
            ? (isDarkMode ? '#D1D5DB' : '#4B5563')
            : (disabled
              ? (isDarkMode ? '#9ca3af' : '#6b7280')
              : (isDarkMode ? '#F1F7FF' : '#0D0D0D')),
          fontSize: rem(0.78, 0.4, 1.05),
          lineHeight: rem(1.05),
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, ...sliderCssVars }}>
      {/* Exit Multiple */}
      <div
        id="Exit Multiple"
        style={{
          ...commonSectionStyle,
          opacity: isDateRangeLocked && isExitPriceVisible ? 1 : exitDisabledOpacity,
          pointerEvents: exitDisabledPointerEvents,
          padding: `${rem(0.1)} 0rem`,
          paddingBottom: '5px',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: exitSectionFlexGrow,
          flexShrink: 1,
          flexBasis: 0,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <h3
          style={{
            color: exitTextColor,
            fontSize: rem(0.75, 0.375, 0.95),
            fontWeight: 'bold',
            marginBottom: rem(0.3125),
            marginTop: 0,
            lineHeight: rem(0.95),
            paddingBottom: pxScaled(7, 4, 10),
            opacity: exitDisabledOpacity,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: rem(0.1, 0.06, 0.16),
            paddingTop: '2px',
            position: 'relative',
            textAlign: 'center',
          }}
        >
          <span
            style={{
              width: rem(0.26, 0.14, 0.36),
              height: rem(0.26, 0.14, 0.36),
              borderRadius: '50%',
              backgroundColor: exitPriceElement?.color ?? '#000000',
              display: 'inline-block',
              position: 'absolute',
              left: '4px',
              top: '4px',
            }}
          />
          Exit Multiple
        </h3>

        {exitPriceElement && (() => {
            const element = exitPriceElement;
            const currentValue = exitAdjustments[element.id] ?? 0;
            const currentText = exitAdjustmentTexts[element.id] ?? currentValue.toFixed(2);
            return (
              <div
                key={`exit-${element.id}`}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                  flex: '1 1 auto',
                  minHeight: 0,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <span style={{ fontSize: rem(0.75, 0.4, 0.95), color: exitTextColor }}>{formatPercent(exitMax)}</span>
                </div>

                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: sliderAreaGap,
                    padding: sliderContainerPadding,
                    boxSizing: 'border-box',
                    flex: '1 1 auto',
                    minHeight: 0,
                  }}
                >
                  <div
                    ref={exitSliderTrackRef}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      flex: '1 1 auto',
                      minHeight: 0,
                    }}
                  >
                    <input
                      type="range"
                      className="exit-slider slider-125"
                      min={String(exitMin)}
                      max={String(exitMax)}
                      step="0.01"
                      disabled={isExitDisabled}
                      value={currentValue}
                      onChange={(e) => {
                        if (isExitDisabled) return;
                        const nextValue = parseFloat(e.target.value);
                        setExitAdjustments((prev) => ({ ...prev, [element.id]: nextValue }));
                        setExitAdjustmentTexts((prev) => ({ ...prev, [element.id]: formatPercent(nextValue) }));
                        onAdjustElement(element.id, nextValue);
                      }}
                      style={{
                        width: exitSliderLength ? `${exitSliderLength + 14}px` : sliderWidth,
                        outline: 'none',
                        cursor: isExitPriceVisible ? 'pointer' : 'not-allowed',
                        transform: 'rotate(-90deg)',
                        transformOrigin: 'center',
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '5px',
                      marginTop: exitBottomLabelMarginTop,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <span style={{ fontSize: rem(0.75, 0.4, 0.95), color: exitTextColor }}>{formatPercent(exitMin)}</span>
                    </div>

                    {renderValueBox(
                      'exit-price-adjustment-input',
                      currentText,
                      (nextText) => {
                        setExitAdjustmentTexts((prev) => ({ ...prev, [element.id]: nextText }));
                        if (isExitDisabled) return;
                        const parsedRatio = parsePercentToRatio(nextText);
                        if (parsedRatio === undefined) return;
                        // While typing, do not clamp to min/max (it makes values like "23%" impossible to enter
                        // because intermediate text like "2" parses to 2% and would snap to the 5% minimum).
                        if (parsedRatio < exitMin || parsedRatio > exitMax) return;
                        setExitAdjustments((prev) => ({ ...prev, [element.id]: parsedRatio }));
                        onAdjustElement(element.id, parsedRatio);
                      },
                      () => {
                        const parsedRatio = parsePercentToRatio(exitAdjustmentTexts[element.id] ?? '');
                        const fallback = exitAdjustments[element.id] ?? exitMin;
                        const clamped = parsedRatio === undefined ? clamp(fallback, exitMin, exitMax) : clamp(parsedRatio, exitMin, exitMax);
                        setExitAdjustmentTexts((prev) => ({ ...prev, [element.id]: formatPercent(clamped) }));
                      },
                      isExitDisabled,
                      isExitDisabled ? 0.6 : 1,
                      exitValueBoxBottom,
                      false,
                      isDateRangeLocked
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
      </div>

      <div
        style={{
          height: rem(0.0625),
          backgroundColor: borderColor,
          width: '100%',
          marginTop: dividerMarginTop,
          flex: '0 0 auto',
        }}
      />

      {/* Entry Multiple */}
      <div
        id="Entry Multiple"
        style={{
          ...commonSectionStyle,
          opacity: isDateRangeLocked && isEntryPriceVisible ? 1 : (isEntryDisabled ? 0.6 : 1),
          pointerEvents: isEntryDisabled ? 'none' : 'auto',
          padding: `${rem(0.1)} 0rem`,
          paddingBottom: '5px',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: entrySectionFlexGrow,
          flexShrink: 1,
          flexBasis: 0,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <h3
          style={{
            color: entryTextColor,
            fontSize: rem(0.75, 0.375, 0.95),
            fontWeight: 'bold',
            marginBottom: rem(0.3125),
            marginTop: 0,
            lineHeight: rem(0.95),
            paddingBottom: pxScaled(7, 4, 10),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: rem(0.1, 0.06, 0.16),
            paddingTop: '2px',
            position: 'relative',
            textAlign: 'center',
          }}
        >
          <span
            style={{
              width: rem(0.26, 0.14, 0.36),
              height: rem(0.26, 0.14, 0.36),
              borderRadius: '50%',
              backgroundColor: entryPriceColor,
              display: 'inline-block',
              position: 'absolute',
              left: '4px',
              top: '4px',
            }}
          />
          Entry Multiple
        </h3>

        {entryPriceElement && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: rem(0.75),
              marginBottom: rem(0.975),
              marginTop: rem(-0.375),
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: rem(0.37, 0.42, 0.55),
                height: rem(0.37, 0.42, 0.55),
                borderRadius: '50%',
                backgroundColor: entryPriceColor,
                border: '1px solid #ffffff',
              }}
            />
          </div>
        )}

        {entryPriceElement && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              flex: '1 1 auto',
              minHeight: 0,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <span style={{ fontSize: rem(0.75, 0.4, 0.95), color: entryTextColor }}>{formatPercent(entryMin)}</span>
            </div>

            <div
              style={{
                position: 'relative',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: sliderAreaGap,
                padding: sliderContainerPadding,
                boxSizing: 'border-box',
                flex: '1 1 auto',
                minHeight: 0,
              }}
            >
              <div
                ref={entrySliderTrackRef}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  flex: '1 1 auto',
                  minHeight: 0,
                }}
              >
                <input
                  type="range"
                  className="exit-slider slider-125"
                  min={String(entryMin)}
                  max={String(entryMax)}
                  step="0.01"
                  disabled={isEntryDisabled}
                  value={entryAdjustments[entryPriceElement.id] ?? entryMin}
                  onChange={(e) => {
                    if (isEntryDisabled) return;
                    const nextValue = clamp(parseFloat(e.target.value), entryMin, entryMax);
                    setEntryAdjustments((prev) => ({ ...prev, [entryPriceElement.id]: nextValue }));
                    setEntryAdjustmentTexts((prev) => ({ ...prev, [entryPriceElement.id]: formatPercent(nextValue) }));
                    onAdjustElement(entryPriceElement.id, nextValue);
                  }}
                  style={{
                    width: entrySliderLength ? `${entrySliderLength + 14}px` : sliderWidth,
                    outline: 'none',
                    cursor: isEntryPriceVisible ? 'pointer' : 'not-allowed',
                    // Inverted orientation: min (0.05) at top, max (0.95) at bottom.
                    transform: 'rotate(90deg)',
                    transformOrigin: 'center',
                    opacity: isEntryPriceVisible ? 1 : 0.6,
                  }}
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '5px',
                  marginTop: entry95MarginTop,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <span style={{ fontSize: rem(0.75, 0.4, 0.95), color: entryTextColor }}>{formatPercent(entryMax)}</span>
                </div>

                {(() => {
                  const currentValue = entryAdjustments[entryPriceElement.id] ?? 0;
                  const currentText = entryAdjustmentTexts[entryPriceElement.id] ?? formatPercent(currentValue);
                  return renderValueBox(
                    'entry-price-adjustment-input',
                    currentText,
                    (nextText) => {
                      setEntryAdjustmentTexts((prev) => ({ ...prev, [entryPriceElement.id]: nextText }));
                      if (isEntryDisabled) return;
                      const parsedRatio = parsePercentToRatio(nextText);
                      if (parsedRatio === undefined) return;
                      // Same behavior as Exit: don't clamp while typing.
                      if (parsedRatio < entryMin || parsedRatio > entryMax) return;
                      setEntryAdjustments((prev) => ({ ...prev, [entryPriceElement.id]: parsedRatio }));
                      onAdjustElement(entryPriceElement.id, parsedRatio);
                    },
                    () => {
                      const parsedRatio = parsePercentToRatio(entryAdjustmentTexts[entryPriceElement.id] ?? '');
                      const fallback = entryAdjustments[entryPriceElement.id] ?? entryMin;
                      const clamped = parsedRatio === undefined ? clamp(fallback, entryMin, entryMax) : clamp(parsedRatio, entryMin, entryMax);
                      setEntryAdjustmentTexts((prev) => ({ ...prev, [entryPriceElement.id]: formatPercent(clamped) }));
                    },
                    isEntryDisabled,
                    isEntryDisabled ? 0.6 : 1,
                    entryValueBoxBottom,
                    false,
                    isDateRangeLocked && isEntryPriceVisible
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RightAdjustmentPanel;
